import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  LineChart, Line, PieChart, Pie, Cell, CartesianGrid, Legend,
} from "recharts";

import DeprecationBanner from "./DeprecationBanner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const TIER_COLORS = { high: "#F59E0B", medium: "#f59e0b", low: "#6b7280" };
const TIER_BG = { high: "bg-emerald-500/10 border-emerald-500/30", medium: "bg-amber-500/10 border-amber-500/30", low: "bg-slate-700/50 border-slate-600" };
const TYPE_ICONS = {
  CREATIVE_PERFORMANCE: "palette", AUDIENCE_SEGMENT: "users", COMMISSION_THRESHOLD: "dollar-sign",
  CAMPAIGN_LONGEVITY: "clock",
};
const STATUS_COLORS = {
  generated: "text-blue-400", surfaced: "text-amber-400", tested: "text-purple-400",
  validated: "text-emerald-400", invalidated: "text-red-400", expired: "text-slate-500",
};

const TierBadge = ({ tier }) => (
  <span
    data-testid={`tier-badge-${tier}`}
    className={`px-2 py-0.5 rounded text-xs font-bold uppercase ${
      tier === "high" ? "bg-emerald-500/20 text-emerald-400" :
      tier === "medium" ? "bg-amber-500/20 text-amber-400" : "bg-slate-600/50 text-slate-400"
    }`}
  >{tier}</span>
);

// ============================================================================
// SECTION TABS
// ============================================================================

const TABS = [
  { id: "predictions", label: "Active Predictions" },
  { id: "hypotheses", label: "Hypotheses" },
  { id: "accuracy", label: "Accuracy" },
  { id: "patterns", label: "Creative Patterns" },
  { id: "seasonal", label: "Seasonal Alerts" },
];

// ============================================================================
// MAIN DASHBOARD
// ============================================================================

const PredictionsDashboard = () => {
  const [tab, setTab] = useState("predictions");
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/predictions/summary`).then(r => setSummary(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  return (
    <div className="space-y-6" data-testid="predictions-dashboard">
      <DeprecationBanner surface="Predictive Intelligence" />
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Predictive Intelligence</h2>
          <p className="text-slate-400 text-sm mt-1">
            {summary ? `${summary.total_predictions} predictions | ${summary.active} active | ${summary.by_tier?.high || 0} HIGH confidence` : "Loading..."}
          </p>
        </div>
        <div className="flex gap-2">
          <button
            data-testid="generate-predictions-btn"
            onClick={async () => { await axios.post(`${API}/predictions/generate`); window.location.reload(); }}
            className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-500 text-white text-xs rounded-lg transition-colors"
          >Regenerate All</button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-slate-900/50 p-1 rounded-lg border border-slate-800">
        {TABS.map(t => (
          <button
            key={t.id}
            data-testid={`tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`flex-1 px-3 py-2 rounded-md text-xs font-medium transition-colors ${
              tab === t.id ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/30" : "text-slate-400 hover:text-white"
            }`}
          >{t.label}</button>
        ))}
      </div>

      {/* Content */}
      {tab === "predictions" && <ActivePredictions />}
      {tab === "hypotheses" && <HypothesesSection />}
      {tab === "accuracy" && <AccuracySection />}
      {tab === "patterns" && <PatternsSection />}
      {tab === "seasonal" && <SeasonalSection />}
    </div>
  );
};

// ============================================================================
// ACTIVE PREDICTIONS
// ============================================================================

const ActivePredictions = () => {
  const [predictions, setPredictions] = useState([]);
  const [filterType, setFilterType] = useState("");
  const [loading, setLoading] = useState(true);

  const fetchPredictions = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filterType) params.set("prediction_type", filterType);
      params.set("limit", "100");
      const { data } = await axios.get(`${API}/predictions/active?${params}`);
      setPredictions(data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [filterType]);

  useEffect(() => { fetchPredictions(); }, [fetchPredictions]);

  const grouped = {};
  predictions.forEach(p => {
    const t = p.prediction_type;
    if (!grouped[t]) grouped[t] = [];
    grouped[t].push(p);
  });

  // OFFER_MOMENTUM retired by founder decision 2026-08-12 — nothing generates new ones and
  // none are unresolved, so its filter would always be empty. Historical accuracy for the
  // type still appears in the Accuracy tab, which groups by prediction_type generically.
  const types = ["CREATIVE_PERFORMANCE", "AUDIENCE_SEGMENT", "COMMISSION_THRESHOLD", "CAMPAIGN_LONGEVITY"];

  return (
    <div className="space-y-4" data-testid="active-predictions-section">
      <div className="flex gap-2 flex-wrap">
        <button onClick={() => setFilterType("")} className={`px-3 py-1 rounded text-xs ${!filterType ? "bg-emerald-500/20 text-emerald-400" : "bg-slate-800 text-slate-400"}`}>All</button>
        {types.map(t => (
          <button key={t} onClick={() => setFilterType(t)} className={`px-3 py-1 rounded text-xs ${filterType === t ? "bg-emerald-500/20 text-emerald-400" : "bg-slate-800 text-slate-400"}`}>
            {t.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())}
          </button>
        ))}
      </div>

      {loading ? <p className="text-slate-500 text-sm">Loading predictions...</p> :
        predictions.length === 0 ? <p className="text-slate-500 text-sm">No active predictions.</p> :
        types.filter(t => !filterType || filterType === t).map(type => (
          grouped[type]?.length > 0 && (
            <div key={type} className="space-y-2">
              <h3 className="text-sm font-semibold text-slate-300 uppercase tracking-wider">
                {type.replace(/_/g, " ")} ({grouped[type].length})
              </h3>
              {grouped[type].map(pred => (
                <PredictionCard key={pred.prediction_id} prediction={pred} onAction={fetchPredictions} />
              ))}
            </div>
          )
        ))
      }
    </div>
  );
};

const PredictionCard = ({ prediction, onAction }) => {
  const navigate = useNavigate();
  const [showOverride, setShowOverride] = useState(false);
  const [overrideReason, setOverrideReason] = useState("Creative fatigue concern");
  const [overrideText, setOverrideText] = useState("");
  const [acting, setActing] = useState(false);
  const [explanation, setExplanation] = useState(null);
  const [loadingExplain, setLoadingExplain] = useState(false);
  const [showWhy, setShowWhy] = useState(false);

  const handleDecision = async (decision) => {
    setActing(true);
    try {
      const params = new URLSearchParams({
        decision_type: prediction.prediction_type === "CAMPAIGN_LONGEVITY" ? "KILL" : "CREATIVE_APPROVAL",
        operator_decision: decision,
        system_recommendation: prediction.prediction_value?.substring(0, 200) || "",
        prediction_id: prediction.prediction_id,
      });
      if (prediction.offer_id) params.set("offer_id", prediction.offer_id);
      if (prediction.campaign_id) params.set("campaign_id", prediction.campaign_id);
      if (decision === "OVERRIDDEN") {
        params.set("override_reason", overrideReason);
        if (overrideText) params.set("reasoning", overrideText);
      }
      await axios.post(`${API}/decisions?${params}`);
      onAction();
    } catch (e) { console.error(e); }
    finally { setActing(false); setShowOverride(false); }
  };

  const fetchExplanation = async () => {
    if (explanation) { setShowWhy(!showWhy); return; }
    setLoadingExplain(true);
    setShowWhy(true);
    try {
      const res = await axios.post(`${API}/predictions/explain`, {
        prediction_value: prediction.prediction_value || "",
        reasoning: prediction.reasoning || "",
        prediction_type: prediction.prediction_type || "",
        confidence_tier: prediction.confidence_tier || "",
      });
      setExplanation(res.data.explanation);
    } catch (e) {
      setExplanation("Unable to generate explanation.");
    } finally {
      setLoadingExplain(false);
    }
  };

  // Determine link destination based on prediction type
  const getLinkInfo = () => {
    const p = prediction;
    const type = p.prediction_type;
    const offerId = p.offer_id;
    const campaignId = p.campaign_id;

    if (type === "CREATIVE_PERFORMANCE" || type === "AUDIENCE_SEGMENT" || type === "CAMPAIGN_LONGEVITY") {
      if (campaignId) return { path: `/campaigns`, label: "View Campaign", enabled: true };
      if (offerId) return { path: `/offers/${offerId}`, label: "View Offer", enabled: true };
    }
    if (type === "COMMISSION_THRESHOLD") {
      if (offerId?.startsWith("gro_")) return { path: `/ghost-recon`, label: "View in Ghost Recon", enabled: true };
      if (offerId) return { path: `/offers/${offerId}`, label: "View Offer", enabled: true };
    }
    return { path: null, label: "View details", enabled: false };
  };

  const linkInfo = getLinkInfo();

  const p = prediction;
  return (
    <div data-testid={`prediction-card-${p.prediction_id}`} className={`p-4 rounded-lg border ${TIER_BG[p.confidence_tier]} space-y-2`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <TierBadge tier={p.confidence_tier} />
            <span className="text-xs text-slate-500">{(p.confidence_score * 100).toFixed(0)}% confidence</span>
          </div>
          <p className="text-sm text-white leading-relaxed">{p.prediction_value}</p>
          <p className="text-xs text-slate-500 mt-1 italic">{p.reasoning}</p>
        </div>
      </div>

      {/* Clickable Link + Why Button */}
      <div className="flex items-center gap-3 pt-1">
        {linkInfo.enabled ? (
          <button
            onClick={() => navigate(linkInfo.path)}
            className="text-xs text-emerald-400 hover:text-emerald-300 underline underline-offset-2 transition-colors"
            data-testid={`prediction-link-${p.prediction_id}`}
          >
            {linkInfo.label}
          </button>
        ) : (
          <span className="text-xs text-slate-600 cursor-not-allowed" data-testid={`prediction-link-${p.prediction_id}`}>
            {linkInfo.label}
          </span>
        )}
        <button
          onClick={fetchExplanation}
          className="text-xs text-blue-400 hover:text-blue-300 transition-colors"
          data-testid={`why-btn-${p.prediction_id}`}
        >
          {showWhy ? "Hide Why" : "Why?"}
        </button>
      </div>

      {/* Explanation Section */}
      {showWhy && (
        <div className="bg-slate-800/60 rounded-lg p-3 border border-slate-700/50" data-testid={`explanation-${p.prediction_id}`}>
          {loadingExplain ? (
            <div className="flex items-center gap-2">
              <div className="animate-spin rounded-full h-3 w-3 border-t border-b border-emerald-500"></div>
              <span className="text-xs text-slate-400">Generating explanation...</span>
            </div>
          ) : (
            <p className="text-sm text-slate-200 leading-relaxed whitespace-pre-wrap">{explanation}</p>
          )}
        </div>
      )}

      {/* Decision Buttons */}
      <div className="flex items-center gap-2 pt-1">
        <button data-testid={`follow-btn-${p.prediction_id}`} onClick={() => handleDecision("FOLLOWED")} disabled={acting}
          className="px-3 py-1 bg-emerald-600/80 hover:bg-emerald-500 text-white text-xs rounded transition-colors disabled:opacity-50">Follow</button>
        <button data-testid={`override-btn-${p.prediction_id}`} onClick={() => setShowOverride(!showOverride)} disabled={acting}
          className="px-3 py-1 bg-amber-600/80 hover:bg-amber-500 text-white text-xs rounded transition-colors disabled:opacity-50">Override</button>
        <button data-testid={`ignore-btn-${p.prediction_id}`} onClick={() => handleDecision("IGNORED")} disabled={acting}
          className="px-3 py-1 bg-slate-700 hover:bg-slate-600 text-slate-300 text-xs rounded transition-colors disabled:opacity-50">Ignore</button>
      </div>

      {showOverride && (
        <div className="pt-2 space-y-2 border-t border-slate-700/50">
          <select data-testid={`override-reason-${p.prediction_id}`} value={overrideReason} onChange={e => setOverrideReason(e.target.value)}
            className="w-full bg-slate-800 text-white text-xs rounded px-2 py-1.5 border border-slate-700">
            <option>Creative fatigue concern</option>
            <option>Offer cap issue</option>
            <option>Budget constraint</option>
            <option>Gut feel</option>
            <option>Other</option>
          </select>
          <input data-testid={`override-text-${p.prediction_id}`} value={overrideText} onChange={e => setOverrideText(e.target.value)}
            placeholder="Additional reasoning (optional)" className="w-full bg-slate-800 text-white text-xs rounded px-2 py-1.5 border border-slate-700" />
          <button onClick={() => handleDecision("OVERRIDDEN")} disabled={acting}
            className="px-3 py-1 bg-amber-600 hover:bg-amber-500 text-white text-xs rounded transition-colors">Confirm Override</button>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// HYPOTHESES
// ============================================================================

const HypothesesSection = () => {
  const [hyps, setHyps] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(true);

  const fetchHyps = useCallback(async () => {
    const params = statusFilter ? `?status=${statusFilter}` : "";
    const { data } = await axios.get(`${API}/creative-intelligence/hypotheses${params}`);
    setHyps(data);
    setLoading(false);
  }, [statusFilter]);

  useEffect(() => { fetchHyps(); }, [fetchHyps]);

  const updateStatus = async (id, status) => {
    await axios.post(`${API}/creative-intelligence/hypotheses/${id}/status?status=${status}`);
    fetchHyps();
  };

  return (
    <div className="space-y-4" data-testid="hypotheses-section">
      <div className="flex gap-2 flex-wrap">
        {["", "generated", "surfaced", "tested", "validated", "invalidated"].map(s => (
          <button key={s} onClick={() => setStatusFilter(s)}
            className={`px-3 py-1 rounded text-xs ${statusFilter === s ? "bg-emerald-500/20 text-emerald-400" : "bg-slate-800 text-slate-400"}`}>
            {s || "All"}
          </button>
        ))}
      </div>

      {loading ? <p className="text-slate-500 text-sm">Loading...</p> :
        hyps.length === 0 ? <p className="text-slate-500 text-sm">No hypotheses found.</p> :
        hyps.map(h => (
          <div key={h.intelligence_id} data-testid={`hypothesis-card-${h.intelligence_id}`}
            className="p-4 rounded-lg border border-slate-700 bg-slate-900/50 space-y-2">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs bg-indigo-500/20 text-indigo-400 px-2 py-0.5 rounded font-medium">
                {h.hypothesis_source_vertical} &rarr; {h.vertical}
              </span>
              <TierBadge tier={h.hypothesis_transfer_confidence || h.confidence_tier} />
              <span className={`text-xs font-medium ${STATUS_COLORS[h.hypothesis_status] || "text-slate-400"}`}>
                {h.hypothesis_status?.toUpperCase()}
              </span>
            </div>
            <p className="text-sm text-white">{h.hypothesis_statement}</p>
            <p className="text-xs text-slate-500">{h.hypothesis_transfer_explanation}</p>
            {h.hypothesis_notes && <p className="text-xs text-slate-400 italic">{h.hypothesis_notes}</p>}

            {["generated", "surfaced", "tested"].includes(h.hypothesis_status) && (
              <div className="flex gap-2 pt-1">
                {h.hypothesis_status === "generated" && (
                  <button onClick={() => updateStatus(h.intelligence_id, "surfaced")} className="px-2 py-1 bg-blue-600/80 text-white text-xs rounded">Surface</button>
                )}
                {h.hypothesis_status === "surfaced" && (
                  <button data-testid={`test-hyp-${h.intelligence_id}`} onClick={() => updateStatus(h.intelligence_id, "tested")} className="px-2 py-1 bg-purple-600/80 text-white text-xs rounded">Mark Tested</button>
                )}
                {h.hypothesis_status === "tested" && (<>
                  <button data-testid={`validate-hyp-${h.intelligence_id}`} onClick={() => updateStatus(h.intelligence_id, "validated")} className="px-2 py-1 bg-emerald-600/80 text-white text-xs rounded">Validate</button>
                  <button data-testid={`invalidate-hyp-${h.intelligence_id}`} onClick={() => updateStatus(h.intelligence_id, "invalidated")} className="px-2 py-1 bg-red-600/80 text-white text-xs rounded">Invalidate</button>
                </>)}
              </div>
            )}
          </div>
        ))
      }
    </div>
  );
};

// ============================================================================
// ACCURACY CHARTS
// ============================================================================

const AccuracySection = () => {
  const [accuracy, setAccuracy] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/predictions/accuracy`).then(r => setAccuracy(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading || !accuracy) return <p className="text-slate-500 text-sm">Loading accuracy data...</p>;

  const typeData = accuracy.by_type?.map(t => ({
    name: t.prediction_type?.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase()).substring(0, 15),
    accuracy: t.accuracy_pct || 0,
    total: t.total_assessed,
  })) || [];

  const tierData = accuracy.by_tier?.map(t => ({
    name: t.confidence_tier?.toUpperCase(),
    accuracy: t.accuracy_pct || 0,
    total: t.total_assessed,
    fill: TIER_COLORS[t.confidence_tier] || "#6b7280",
  })) || [];

  const trendData = accuracy.trend?.map(t => ({
    period: t.period,
    accuracy: t.accuracy_pct || 0,
    total: t.total,
  })) || [];

  return (
    <div className="space-y-6" data-testid="accuracy-section">
      {/* Overall */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Overall Accuracy" value={accuracy.overall?.accuracy_pct != null ? `${accuracy.overall.accuracy_pct}%` : "N/A"} />
        <StatCard label="Total Assessed" value={accuracy.overall?.total_assessed || 0} />
        <StatCard label="Correct" value={accuracy.overall?.correct || 0} color="text-emerald-400" />
        <StatCard label="Incorrect" value={accuracy.overall?.incorrect || 0} color="text-red-400" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* By Type */}
        <div className="bg-slate-900/50 border border-slate-800 rounded-lg p-4">
          <h4 className="text-sm font-semibold text-white mb-3">Accuracy by Prediction Type</h4>
          {typeData.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={typeData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#352F24" />
                <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#9E9880" }} />
                <YAxis tick={{ fontSize: 10, fill: "#9E9880" }} domain={[0, 100]} />
                <Tooltip contentStyle={{ background: "#252218", border: "1px solid #352F24", borderRadius: 8, fontSize: 12 }} />
                <Bar dataKey="accuracy" fill="#F59E0B" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : <p className="text-slate-500 text-xs">No data yet</p>}
        </div>

        {/* By Tier */}
        <div className="bg-slate-900/50 border border-slate-800 rounded-lg p-4">
          <h4 className="text-sm font-semibold text-white mb-3">Accuracy by Confidence Tier</h4>
          {tierData.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={tierData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#352F24" />
                <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#9E9880" }} />
                <YAxis tick={{ fontSize: 10, fill: "#9E9880" }} domain={[0, 100]} />
                <Tooltip contentStyle={{ background: "#252218", border: "1px solid #352F24", borderRadius: 8, fontSize: 12 }} />
                <Bar dataKey="accuracy" radius={[4, 4, 0, 0]}>
                  {tierData.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : <p className="text-slate-500 text-xs">No data yet</p>}
        </div>
      </div>

      {/* Trend */}
      {trendData.length > 0 && (
        <div className="bg-slate-900/50 border border-slate-800 rounded-lg p-4">
          <h4 className="text-sm font-semibold text-white mb-3">Accuracy Trend Over Time</h4>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={trendData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#352F24" />
              <XAxis dataKey="period" tick={{ fontSize: 10, fill: "#9E9880" }} />
              <YAxis tick={{ fontSize: 10, fill: "#9E9880" }} domain={[0, 100]} />
              <Tooltip contentStyle={{ background: "#252218", border: "1px solid #352F24", borderRadius: 8, fontSize: 12 }} />
              <Line type="monotone" dataKey="accuracy" stroke="#F59E0B" strokeWidth={2} dot={{ fill: "#F59E0B" }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};

const StatCard = ({ label, value, color = "text-white" }) => (
  <div className="bg-slate-900/50 border border-slate-800 rounded-lg p-3">
    <p className="text-xs text-slate-500 mb-1">{label}</p>
    <p className={`text-xl font-bold ${color}`}>{value}</p>
  </div>
);

// ============================================================================
// CREATIVE PATTERNS
// ============================================================================

const PatternsSection = () => {
  const navigate = useNavigate();
  const [patterns, setPatterns] = useState([]);
  const [fatigue, setFatigue] = useState([]);
  const [vertFilter, setVertFilter] = useState("");
  const [channelFilter, setChannelFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [expandedPattern, setExpandedPattern] = useState(null);

  useEffect(() => {
    const fetch = async () => {
      const params = new URLSearchParams();
      if (vertFilter) params.set("vertical", vertFilter);
      if (channelFilter) params.set("channel", channelFilter);
      const [pRes, fRes] = await Promise.all([
        axios.get(`${API}/creative-intelligence/top-patterns?${params}`),
        axios.get(`${API}/creative-intelligence/fatigue-benchmarks?${params}`),
      ]);
      setPatterns(pRes.data);
      setFatigue(fRes.data);
      setLoading(false);
    };
    fetch();
  }, [vertFilter, channelFilter]);

  const verticals = [...new Set(patterns.map(p => p.vertical))];
  const channels = [...new Set(patterns.map(p => p.channel))];

  const toggleExpand = (id) => {
    setExpandedPattern(expandedPattern === id ? null : id);
  };

  return (
    <div className="space-y-4" data-testid="patterns-section">
      <div className="flex gap-2 flex-wrap">
        <select value={vertFilter} onChange={e => setVertFilter(e.target.value)}
          data-testid="pattern-vertical-filter"
          className="bg-slate-800 text-white text-xs rounded px-2 py-1.5 border border-slate-700">
          <option value="">All Verticals</option>
          {verticals.map(v => <option key={v} value={v}>{v}</option>)}
        </select>
        <select value={channelFilter} onChange={e => setChannelFilter(e.target.value)}
          data-testid="pattern-channel-filter"
          className="bg-slate-800 text-white text-xs rounded px-2 py-1.5 border border-slate-700">
          <option value="">All Channels</option>
          {channels.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      {loading ? <p className="text-slate-500 text-sm">Loading...</p> :
        <div className="space-y-2">
          {patterns.map((p, i) => {
            const isExpanded = expandedPattern === (p.intelligence_id || i);
            const hasCampaigns = p.sample_size > 0 && p.campaign_ids?.length > 0;
            return (
              <div key={p.intelligence_id || i}>
                <button
                  onClick={() => toggleExpand(p.intelligence_id || i)}
                  data-testid={`pattern-card-${p.intelligence_id}`}
                  className={`w-full text-left p-3 rounded-lg border transition-colors ${
                    isExpanded 
                      ? "border-emerald-500/40 bg-slate-900/70" 
                      : "border-slate-700 bg-slate-900/50 hover:border-slate-600"
                  }`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-semibold text-emerald-400">{p.vertical}</span>
                        <span className="text-xs text-slate-600">/</span>
                        <span className="text-xs text-slate-400">{p.channel}</span>
                        <TierBadge tier={p.confidence_tier} />
                        <svg className={`w-3 h-3 text-slate-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                      <p className="text-sm text-white font-medium">
                        {p.vehicle} + {p.message} + {p.visual_interrupt}
                      </p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-lg font-bold text-emerald-400">${p.avg_cpa}</p>
                      <p className="text-xs text-slate-500">avg CPA</p>
                    </div>
                  </div>
                  <div className="flex gap-4 mt-2 text-xs text-slate-400">
                    <span>CTR: {p.avg_ctr}%</span>
                    <span>Thumb Stop: {p.avg_thumb_stop_rate}%</span>
                    <span>Watch: {p.avg_watch_time}s</span>
                    <span>Hook Ret: {p.avg_hook_retention}%</span>
                    <span>Campaigns: {p.sample_size}</span>
                    <span>Win Rate: {(p.win_rate * 100).toFixed(0)}%</span>
                  </div>
                  {p.avg_fatigue_days && (
                    <div className="flex gap-4 mt-1 text-xs">
                      <span className="text-amber-400">Fatigue: {p.avg_fatigue_days}d avg</span>
                      {p.avg_hook_injection_runway && (
                        <span className="text-blue-400">Hook injection runway: +{p.avg_hook_injection_runway}d</span>
                      )}
                    </div>
                  )}
                </button>

                {/* Expanded Detail View */}
                {isExpanded && (
                  <div className="mt-1 p-4 bg-slate-800/50 border border-slate-700/50 rounded-lg space-y-3" data-testid={`pattern-detail-${p.intelligence_id}`}>
                    {hasCampaigns ? (
                      <>
                        <h4 className="text-sm font-medium text-white">Campaigns using this pattern</h4>
                        <div className="space-y-2">
                          {(p.campaign_ids || []).map((cid, ci) => (
                            <div key={ci} className="flex items-center justify-between p-2 bg-slate-900/50 rounded border border-slate-700/30">
                              <div>
                                <p className="text-xs text-white font-medium">{cid}</p>
                                <p className="text-xs text-slate-500">Campaign #{ci + 1}</p>
                              </div>
                              <button
                                onClick={() => navigate('/campaigns')}
                                className="text-xs text-emerald-400 hover:text-emerald-300"
                              >
                                View
                              </button>
                            </div>
                          ))}
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 pt-2">
                          <div className="bg-slate-900/50 rounded p-2">
                            <p className="text-xs text-slate-500">Avg CPA</p>
                            <p className="text-sm font-bold text-emerald-400">${p.avg_cpa}</p>
                          </div>
                          <div className="bg-slate-900/50 rounded p-2">
                            <p className="text-xs text-slate-500">Win Rate</p>
                            <p className="text-sm font-bold text-emerald-400">{(p.win_rate * 100).toFixed(0)}%</p>
                          </div>
                          <div className="bg-slate-900/50 rounded p-2">
                            <p className="text-xs text-slate-500">Sample Size</p>
                            <p className="text-sm font-bold text-white">{p.sample_size}</p>
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="text-center py-4">
                        <p className="text-sm text-slate-400">No campaign data yet</p>
                        <p className="text-xs text-slate-500 mt-1">
                          This pattern will link to real campaigns as data accumulates
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      }

      {fatigue.length > 0 && (
        <div className="bg-slate-900/50 border border-slate-800 rounded-lg p-4 mt-4">
          <h4 className="text-sm font-semibold text-white mb-3">Creative Fatigue Benchmarks</h4>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={fatigue.map(f => ({
              name: `${f.vertical?.substring(0, 10)} ${f.channel}`,
              fatigue: f.avg_fatigue_days,
              runway: f.avg_hook_injection_runway || 0,
            }))}>
              <CartesianGrid strokeDasharray="3 3" stroke="#352F24" />
              <XAxis dataKey="name" tick={{ fontSize: 9, fill: "#9E9880" }} />
              <YAxis tick={{ fontSize: 10, fill: "#9E9880" }} label={{ value: "Days", angle: -90, position: "insideLeft", style: { fill: "#9E9880", fontSize: 10 } }} />
              <Tooltip contentStyle={{ background: "#252218", border: "1px solid #352F24", borderRadius: 8, fontSize: 12 }} />
              <Legend wrapperStyle={{ fontSize: 10 }} />
              <Bar dataKey="fatigue" name="Avg Fatigue Days" fill="#f59e0b" radius={[4, 4, 0, 0]} />
              <Bar dataKey="runway" name="Hook Injection Runway" fill="#3b82f6" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// SEASONAL ALERTS
// ============================================================================

const SeasonalSection = () => {
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/seasonal/alerts`).then(r => setAlerts(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const alertTypeLabels = {
    active_peak: { label: "IN PEAK NOW", color: "bg-red-500/20 text-red-400 border-red-500/30" },
    volume_wave: { label: "WAVE BUILDING", color: "bg-orange-500/20 text-orange-400 border-orange-500/30" },
    upcoming_peak: { label: "UPCOMING PEAK", color: "bg-blue-500/20 text-blue-400 border-blue-500/30" },
    end_of_window: { label: "END OF WINDOW", color: "bg-slate-600/30 text-slate-400 border-slate-600" },
  };

  const handleAlertClick = (vertical) => {
    // Navigate to Offers page pre-filtered by this vertical
    navigate(`/offers?vertical=${encodeURIComponent(vertical)}`);
  };

  return (
    <div className="space-y-3" data-testid="seasonal-section">
      {loading ? <p className="text-slate-500 text-sm">Loading seasonal data...</p> :
        alerts.length === 0 ? <p className="text-slate-500 text-sm">No seasonal alerts.</p> :
        alerts.map((a, i) => {
          const cfg = alertTypeLabels[a.alert_type] || alertTypeLabels.upcoming_peak;
          return (
            <button
              key={i}
              onClick={() => handleAlertClick(a.vertical)}
              data-testid={`seasonal-alert-${i}`}
              className={`w-full text-left p-3 rounded-lg border ${cfg.color} space-y-1 hover:brightness-110 transition-all cursor-pointer group`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-0.5 rounded text-xs font-bold uppercase ${cfg.color}`}>{cfg.label}</span>
                  <span className="text-sm font-medium text-white">{a.vertical}</span>
                  {a.weeks_until_peak > 0 && <span className="text-xs text-slate-400">~{Math.round(a.weeks_until_peak)} weeks</span>}
                </div>
                <span className="text-xs text-slate-500 group-hover:text-emerald-400 transition-colors">
                  View Offers &rarr;
                </span>
              </div>
              <p className="text-xs text-slate-300">{a.recommended_action}</p>
              {a.increase_pct && <p className="text-xs text-orange-400">Volume: +{a.increase_pct}% week-over-week ({a.recent_count} vs {a.prior_count})</p>}
            </button>
          );
        })
      }
    </div>
  );
};

export default PredictionsDashboard;
